import { PAGE_SIZES } from '@/constants/pagination';
import { Role } from '@/models/AccountEnums';
import { MimeType } from '@/utils/fileValidation';
import { triggerBlobDownload } from '@/utils/triggerBlobDownload';
import {
  buildCusipsQueryParams,
  type CusipsListRequest,
} from './buildCusipsQueryParams';
import { ADVISOR_HOLDINGS_URL } from './classificationEndpoints';
import type {
  UserExportColumn,
  UserExportRequestBody,
} from './exportHoldingsWithoutCusips';

export const ADVISOR_HOLDINGS_EXPORT_NAME = 'AssetClassificationByHolding';
export const ADVISOR_HOLDINGS_EXPORT_FILENAME = `${ADVISOR_HOLDINGS_EXPORT_NAME}.xls`;

export type AdvisorHoldingsExportListArgs = CusipsListRequest & {
  OnlyUnclassified: boolean;
};

export function buildAdvisorHoldingsExportBody(args: {
  listArgs: AdvisorHoldingsExportListArgs;
  columns: UserExportColumn[];
  token: string;
}): UserExportRequestBody {
  const { OnlyUnclassified, ...list } = args.listArgs;
  const params = buildCusipsQueryParams({
    ...list,
    Page: 1,
    Limit: PAGE_SIZES.EXPORT,
  });
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    search.set(key, String(value));
  }
  search.set('OnlyUnclassified', String(OnlyUnclassified));
  search.set('IsExport', 'true');
  return {
    // /api/user/Export requires a DataUrl that starts with "api/" (no leading slash).
    DataUrl: `${ADVISOR_HOLDINGS_URL.slice(1)}?${search.toString()}`,
    Token: args.token,
    Columns: args.columns,
    FileName: ADVISOR_HOLDINGS_EXPORT_NAME,
    CurrentRole: Role.Advisor,
    ChildProperty: null,
  };
}

export type AdvisorHoldingsExportResult = {
  success: true;
  filename: string;
};

/** A spreadsheet download never has a JSON, HTML or plain text content type. */
function isSpreadsheetBlob(blob: Blob): boolean {
  return !/json|html|text/i.test(blob.type);
}

/**
 * Response handler for the export request: the Blob never reaches the Redux store.
 * fetchBaseQuery runs it before checking the status, so a non-ok response returns
 * its text (reported as an error by RTK) and is never downloaded. An ok response
 * that is not a spreadsheet throws, which RTK reports as a parsing error.
 */
export async function completeAdvisorHoldingsExport(
  response: Pick<Response, 'ok' | 'blob' | 'text'>,
): Promise<AdvisorHoldingsExportResult | string> {
  if (!response.ok) {
    return response.text();
  }
  const blob = await response.blob();
  if (!isSpreadsheetBlob(blob)) {
    throw new Error('Invalid data type returned, expected an xls file');
  }
  await triggerBlobDownload(
    blob,
    ADVISOR_HOLDINGS_EXPORT_FILENAME,
    MimeType.Xls,
  );
  return { success: true, filename: ADVISOR_HOLDINGS_EXPORT_FILENAME };
}
